import type {
    JobTypeConfig,
} from "@mrflark/core/jobs";

export const SPLJobType = {
    ProcessMedia: "process_media",
    ComputeHash: "compute_hash",
    CheckDuplicate: "check_duplicate",
    NormalizeFormat: "normalize_format",
    ExtractOcr: "extract_ocr",
    DetectWatermark: "detect_watermark",
    AiAnalysis: "ai_analysis",
    GenerateThumbnail: "generate_thumbnail",
    FinalizeProcessing: "finalize_processing",
    ScanUnprocessed: "scan_unprocessed",
    RebuildFtsIndex: "rebuild_fts_index",
    CleanupOldJobs: "cleanup_old_jobs",
    ReanalyzeAll: "reanalyze_all",
    BulkTagOperation: "bulk_tag_operation",
    ReanalyzeMedia: "reanalyze_media",
    RebuildHashIndex: "rebuild_hash_index",
    AuditReviewQueue: "audit_review_queue",
    ComputeVideoDedup: "compute_video_dedup",
    ComputeSmallVariant: "compute_small_variant",
    ExtractAudioTrack: "extract_audio_track",
    ClassifyAudioTracks: "classify_audio_tracks",
    FingerprintAudioTracks: "fingerprint_audio_tracks",
} as const;

export type SPLJobType = typeof SPLJobType[keyof typeof SPLJobType];

const MEDIA_ID_FIELD = {
    key: "mediaId",
    label: "Media ID",
    type: "string" as const,
    required: true,
    placeholder: "UUID of media item",
};

export const SPL_JOB_TYPE_CONFIGS: JobTypeConfig[] = [
    {
        jobType: SPLJobType.ProcessMedia,
        label: "Process Media",
        manuallyInvocable: true,
        payloadFields: [MEDIA_ID_FIELD],
    },
    {
        jobType: SPLJobType.ComputeHash,
        label: "Compute Hash",
        manuallyInvocable: true,
        payloadFields: [MEDIA_ID_FIELD],
    },
    {
        jobType: SPLJobType.CheckDuplicate,
        label: "Check Duplicate",
        manuallyInvocable: true,
        payloadFields: [MEDIA_ID_FIELD],
    },
    {
        jobType: SPLJobType.NormalizeFormat,
        label: "Normalize Format",
        manuallyInvocable: true,
        payloadFields: [MEDIA_ID_FIELD],
    },
    {
        jobType: SPLJobType.ExtractOcr,
        label: "Extract OCR",
        manuallyInvocable: true,
        payloadFields: [MEDIA_ID_FIELD],
    },
    {
        jobType: SPLJobType.DetectWatermark,
        label: "Detect Watermark",
        manuallyInvocable: true,
        payloadFields: [MEDIA_ID_FIELD],
    },
    {
        jobType: SPLJobType.AiAnalysis,
        label: "AI Analysis",
        manuallyInvocable: true,
        payloadFields: [MEDIA_ID_FIELD],
    },
    {
        jobType: SPLJobType.GenerateThumbnail,
        label: "Generate Thumbnail",
        manuallyInvocable: true,
        payloadFields: [MEDIA_ID_FIELD],
    },
    {
        jobType: SPLJobType.FinalizeProcessing,
        label: "Finalize Processing",
        manuallyInvocable: true,
        payloadFields: [MEDIA_ID_FIELD],
    },
    {
        jobType: SPLJobType.ScanUnprocessed,
        label: "Scan Unprocessed",
        manuallyInvocable: true,
    },
    {
        jobType: SPLJobType.RebuildFtsIndex,
        label: "Rebuild FTS Index",
        manuallyInvocable: true,
    },
    {
        jobType: SPLJobType.CleanupOldJobs,
        label: "Cleanup Old Jobs",
        manuallyInvocable: true,
    },
    {
        jobType: SPLJobType.ReanalyzeAll,
        label: "Re-Analyze All Media",
        manuallyInvocable: true,
    },
    {
        jobType: SPLJobType.BulkTagOperation,
        label: "Bulk Tag Operation",
        manuallyInvocable: false,
    },
    {
        jobType: SPLJobType.ReanalyzeMedia,
        label: "Re-Analyze Media",
        manuallyInvocable: false,
    },
    {
        jobType: SPLJobType.RebuildHashIndex,
        label: "Rebuild Hash Index",
        manuallyInvocable: true,
    },
    {
        jobType: SPLJobType.AuditReviewQueue,
        label: "Audit Review Queue",
        manuallyInvocable: true,
    },
    {
        jobType: SPLJobType.ComputeVideoDedup,
        label: "Compute Video Dedup",
        manuallyInvocable: true,
        payloadFields: [MEDIA_ID_FIELD],
    },
    {
        jobType: SPLJobType.ComputeSmallVariant,
        label: "Compute Small Variant",
        manuallyInvocable: true,
        payloadFields: [MEDIA_ID_FIELD],
    },
    {
        jobType: SPLJobType.ExtractAudioTrack,
        label: "Extract Audio Track",
        manuallyInvocable: true,
        payloadFields: [MEDIA_ID_FIELD],
    },
    {
        jobType: SPLJobType.ClassifyAudioTracks,
        label: "Classify Audio Tracks",
        manuallyInvocable: true,
        payloadFields: [MEDIA_ID_FIELD],
    },
    {
        jobType: SPLJobType.FingerprintAudioTracks,
        label: "Fingerprint Audio Tracks",
        manuallyInvocable: true,
        payloadFields: [MEDIA_ID_FIELD],
    },
];

export const SPL_JOB_TYPE_LABELS: Record<string, string> = Object.fromEntries(
    SPL_JOB_TYPE_CONFIGS.map((config) => [config.jobType, config.label]),
);

export const PIPELINE_JOB_TYPES: Set<string> = new Set<string>([
    SPLJobType.ComputeHash,
    SPLJobType.CheckDuplicate,
    SPLJobType.NormalizeFormat,
    SPLJobType.ExtractOcr,
    SPLJobType.DetectWatermark,
    SPLJobType.AiAnalysis,
    SPLJobType.GenerateThumbnail,
    SPLJobType.FinalizeProcessing,
]);

export const VOTE_TYPES = ["like", "dislike", "love"] as const;

export const VOTE_WEIGHTS = {
    like: 1,
    dislike: -1,
    love: 3,
} as const;

export const VOTE_TYPE_LABELS = {
    like: "Like",
    dislike: "Dislike",
    love: "Love",
} as const;
